import { Component, Inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormControl } from '@angular/forms';
import { map, Observable, startWith } from 'rxjs';
import { MediaFileWithProcessing, MediaProcessingType, MediaCategory } from '../media-library.model';
import { MatRadioChange } from '@angular/material/radio';
import { WidgetService } from '../../services/widget.service';
import { SnackBarService } from '../../../core/snack-bar.service';
import { AccountFileResponse } from '../../../campaign/widgets.model';
import { HttpErrorResponse } from '@angular/common/http';

interface SelectedMediaFile {
  file: File;
  processingType: MediaProcessingType;
  preview: string;
  sizeKB: number;
  showResizeOption: boolean;
  hasError: boolean;
  errorMessage: string;
}

export interface UploadMediaFilesModalData {
  photoContainers: string[];
  graphicContainers: string[];
  videoContainers: string[];
  category: MediaCategory;
  collection?: string;
  files?: File[];
  isInBackoffice: boolean;
  supportsVideos: boolean;
}

interface UploadError {
  file: File;
  translationKey: string;
}

export interface UploadMediaFilesModalResponse {
  files: Array<AccountFileResponse>;
  // The category and collection the files were uploaded to, so callers can
  // reflect the selection (e.g. mark the folder in the media library grid).
  category: MediaCategory;
  collection: string | null;
}

@Component({
  selector: 'app-upload-media-modal',
  templateUrl: './upload-media-modal.component.html',
  styleUrl: './upload-media-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class UploadMediaModalComponent implements OnInit {
  selectedFiles: SelectedMediaFile[] = [];
  isDragOver = false;
  category: MediaCategory = MediaCategory.Photos;
  MediaCategory = MediaCategory;
  mediaProcessingType = MediaProcessingType;
  collection?: string = null;
  collections: Array<string> = [];
  collectionControl = new FormControl('');
  filteredOptions: Observable<string[]>;
  loading: boolean = false;
  uploadedFiles: Array<AccountFileResponse> = [];
  uploadErrors: Array<UploadError> = [];
  private uploadedCollection: string | null = null;
  private uploadedCategory: MediaCategory = MediaCategory.Photos;
  videoMp4: string = 'video/mp4';
  videoWebm: string = 'video/webm';
  supportsVideos: boolean = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: UploadMediaFilesModalData,
    private dialogRef: MatDialogRef<UploadMediaModalComponent>,
    private widgetService: WidgetService,
    private snackbarService: SnackBarService,
  ) {}

  ngOnInit() {
    this.filteredOptions = this.collectionControl.valueChanges.pipe(
      startWith(''),
      map((value) => this._filter(value || '')),
    );
    this.category = this.data.category;
    this.collection = this.data.collection;

    // Sync collection value to FormControl
    if (this.collection) {
      this.collectionControl.setValue(this.collection);
    }

    this.supportsVideos = this.data.supportsVideos;

    //This is to prevent saving images to VideoCategory
    if (this.supportsVideos && this.category === MediaCategory.Videos) {
      this.category = MediaCategory.Photos;
    }
    if (this.category === MediaCategory.Photos) {
      this.collections = this.data.photoContainers;
    } else {
      this.collections = this.data.graphicContainers;
    }
    if (this.data.files && this.data.files.length > 0) {
      this.processFiles(this.data.files);
    }
  }

  onFileSelected(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    const input = event.target as HTMLInputElement;
    if (input.files) {
      const files = Array.from(input.files);
      const unsupportedFiles = files.filter((file) => !this.isSupportedFile(file));
      if (unsupportedFiles.length > 0) {
        this.snackbarService.warning('campaign.widget.sidebar.media.file-format-not-supported');
      }
      const mediaFiles = files.filter((file) => this.isSupportedFile(file));

      if (mediaFiles.length > 0) {
        this.processFiles(mediaFiles);
      }
    }
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    const files = Array.from(event.dataTransfer?.files || []);

    const allFilesSupported = files.every((file) => this.isSupportedFile(file));

    if (allFilesSupported && !this.isBulkVideoUpload(files)) {
      this.isDragOver = true;
    }
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;

    const files = Array.from(event.dataTransfer?.files || []);
    const unsupportedFiles = files.filter((file) => !this.isSupportedFile(file));
    if (unsupportedFiles.length > 0) {
      this.snackbarService.warning('campaign.widget.sidebar.media.file-format-not-supported');
    }
    const mediaFiles = files.filter((file) => this.isSupportedFile(file));

    if (mediaFiles.length > 0) {
      this.processFiles(mediaFiles);
    }
  }

  private isVideo(file: File): boolean {
    return file.type === this.videoMp4 || file.type === this.videoWebm;
  }

  private isMediaFile(file: File): boolean {
    return file.type.startsWith('image/') || this.isVideo(file);
  }

  private isSupportedFile(file: File): boolean {
    if (file.type.startsWith('image/')) {
      return true;
    }
    if (this.supportsVideos && this.isVideo(file)) {
      return true;
    }
    return false;
  }

  private isBulkVideoUpload(files: File[]): boolean {
    const newVideoCount = files.filter((file) => this.isVideo(file)).length;

    const existingVideoCount = this.selectedFiles.filter((item) => this.isVideo(item.file)).length;

    return newVideoCount + existingVideoCount > 1;
  }

  private processFiles(files: File[]) {
    const unsupportedFiles = files.filter((file) => !this.isSupportedFile(file));
    if (unsupportedFiles.length > 0) {
      this.snackbarService.warning('campaign.widget.sidebar.media.file-format-not-supported');
    }
    files = files.filter((file) => this.isSupportedFile(file));
    if (files.length === 0) {
      return;
    }

    let hasShownBulkVideoError = false;
    let pendingVideoCount = 0;
    const existingVideoCount = this.selectedFiles.filter((item) => this.isVideo(item.file)).length;

    files.forEach((file) => {
      const isVideoFile = this.isVideo(file);

      if (isVideoFile) {
        if (existingVideoCount + pendingVideoCount >= 1) {
          if (!hasShownBulkVideoError) {
            this.snackbarService.error('campaign.widget.sidebar.media.upload-videos.video-upload-limit');
            hasShownBulkVideoError = true;
          }
          return;
        }
        pendingVideoCount++;
      }

      if (file.size / 1024 > 5120 && (file.type.includes('gif') || file.type.includes('svg'))) {
        this.snackbarService.error('campaign.widget.component.image.messages.file-is-too-large');
        return;
      }

      const maxVideoSizeInBytes = 25 * 1024 * 1024; // 25 MB
      if (file.size > maxVideoSizeInBytes && isVideoFile) {
        this.snackbarService.error('campaign.widget.sidebar.media.upload-videos.video-too-large');
        return;
      }

      const sizeKB = Math.round(file.size / 1024);
      const showResizeOption = !isVideoFile && sizeKB > 500;

      const reader = new FileReader();
      reader.onload = (e) => {
        let processingType = MediaProcessingType.Original;
        if (isVideoFile) {
          processingType = MediaProcessingType.Video;
        } else if (showResizeOption) {
          processingType = MediaProcessingType.Resized;
        }

        const selectedFile: SelectedMediaFile = {
          file,
          processingType,
          preview: e.target?.result as string,
          sizeKB,
          showResizeOption,
          hasError: false,
          errorMessage: '',
        };

        this.selectedFiles.push(selectedFile);

        this.updateCollectionFieldState();
      };
      reader.readAsDataURL(file);
    });
  }

  removeFile(index: number) {
    this.selectedFiles.splice(index, 1);

    this.updateCollectionFieldState();
  }

  onProcessingTypeChange(index: number, processingType: MediaProcessingType) {
    this.selectedFiles[index].processingType = processingType;
  }

  onCategoryChange(e: MatRadioChange) {
    if (e.value === MediaCategory.Photos) {
      this.collections = this.data.photoContainers;
    } else {
      this.collections = this.data.graphicContainers;
    }
    this.collectionControl.setValue('');
  }

  private _filter(value: string): string[] {
    const filterValue = value.toLowerCase();
    return (this.collections || []).filter((option) => option.length > 0 && option.toLowerCase().includes(filterValue));
  }

  uploadMediaFiles() {
    let collection: string = null;
    const collectionValue = this.collectionControl.value;
    if (collectionValue) {
      collection = collectionValue.trim().replace(/[^a-zA-Z0-9._-]/g, '_');
    }
    const result: MediaFileWithProcessing[] = this.selectedFiles.map((selectedFile) => {
      let finalProcessingType = selectedFile.processingType;

      if (selectedFile.processingType !== MediaProcessingType.Video) {
        // Apply correct processing type logic based on file size and user selection
        if (selectedFile.showResizeOption) {
          // Big image (>500KB) - user was prompted for resize option
          if (selectedFile.processingType === MediaProcessingType.Original) {
            // User chose "Keep Original Size" for a big image -> MaxSized
            finalProcessingType = MediaProcessingType.MaxSized;
          } else {
            // User chose "Resize" for a big image -> Resized
            finalProcessingType = MediaProcessingType.Resized;
          }
        } else {
          // Small image (≤500KB) - no prompt needed -> Original
          finalProcessingType = MediaProcessingType.Original;
        }
      }

      return {
        file: selectedFile.file,
        mediaProcessingType: finalProcessingType,
      };
    });

    // Remember where the files went so the caller can mark the folder afterwards.
    // Videos always land in the Videos category regardless of the image radio.
    this.uploadedCollection = collection;
    this.uploadedCategory = result.every((r) => r.mediaProcessingType === MediaProcessingType.Video)
      ? MediaCategory.Videos
      : this.category;

    this.loading = true;
    let completedRequests = 0;
    const totalRequests = result.length;

    result.forEach((mediaFileToUpload, index) => {
      const fileName = mediaFileToUpload.file.name.replace(/[^a-zA-Z0-9._-]/g, '_'); // sanitize filename
      const formData = new FormData();
      formData.append('file', mediaFileToUpload.file, fileName);
      formData.append('mediaProcessingType', mediaFileToUpload.mediaProcessingType);

      let uploadRequest$: Observable<AccountFileResponse>;

      if (mediaFileToUpload.mediaProcessingType === MediaProcessingType.Video) {
        uploadRequest$ = this.data.isInBackoffice
          ? this.widgetService.uploadVideoAdmin(formData, collection)
          : this.widgetService.uploadVideo(formData, collection);
      } else {
        uploadRequest$ = this.data.isInBackoffice
          ? this.widgetService.uploadImageAdmin(formData, this.category, collection)
          : this.widgetService.uploadImage(formData, this.category, collection);
      }

      uploadRequest$.subscribe(
        (response) => {
          completedRequests++;
          this.uploadedFiles.push(response);

          // Check if all requests are completed
          if (completedRequests === totalRequests) {
            this.handleAllUploadsCompleted();
          }
        },
        (error: HttpErrorResponse) => {
          completedRequests++;
          // Handle individual error by creating an error response
          let translationKey = 'campaign.widget.sidebar.media.upload-images.file-generic-error';
          if (error.status === 413) {
            translationKey = 'campaign.widget.sidebar.media.upload-images.file-too-big';
          }
          this.uploadErrors.push({
            file: mediaFileToUpload.file,
            translationKey: translationKey,
          });

          // Update the corresponding selectedFile with error info
          this.selectedFiles[index].hasError = true;
          this.selectedFiles[index].errorMessage = translationKey;

          // Check if all requests are completed
          if (completedRequests === totalRequests) {
            this.handleAllUploadsCompleted();
          }
        },
      );
    });
  }

  private handleAllUploadsCompleted() {
    if (this.uploadErrors.length === 0) {
      const mediaType = this.supportsVideos ? 'videos' : 'images';
      this.snackbarService.success(`campaign.widget.sidebar.media.upload-${mediaType}.upload-success`);
      this.dialogRef.close({
        files: this.uploadedFiles,
        category: this.uploadedCategory,
        collection: this.uploadedCollection,
      } as UploadMediaFilesModalResponse);
    } else {
      this.loading = false;
      this.selectedFiles = this.selectedFiles.filter((x) => x.hasError);
      this.snackbarService.error('campaign.widget.sidebar.media.upload-images.uploading-partial-error');
    }
  }

  get modalTitleKey(): string {
    const mediaType = this.supportsVideos ? 'videos' : 'images';
    const text = this.loading ? 'uploading-title' : 'title';

    return `campaign.widget.sidebar.media.upload-${mediaType}.${text}`;
  }

  cancel() {
    this.dialogRef.close(null);
  }

  triggerFileInput() {
    const fileInput = document.getElementById('upload-multiple-images-dialog-file-input') as HTMLInputElement;
    if (!fileInput) return;
    fileInput.value = '';
    fileInput.click();
  }

  get hasMixedMediaTypes(): boolean {
    if (this.selectedFiles.length === 0) {
      return false;
    }

    const hasVideos = this.selectedFiles.some((file) => this.isVideo(file.file));
    const hasImages = this.selectedFiles.some((file) => !this.isVideo(file.file));

    return hasVideos && hasImages;
  }

  get collectionDisabledTooltip(): string {
    return this.hasMixedMediaTypes ? 'campaign.widget.sidebar.media.upload-images.collection-disabled-mixed-types' : '';
  }

  private updateCollectionFieldState(): void {
    if (this.hasMixedMediaTypes) {
      this.collectionControl.disable();
    } else {
      this.collectionControl.enable();
    }
  }
}
